

































import BaseLoading from '@/components/base/BaseLoading.vue';
import Container from '@/components/Container.vue';
import { PublicCourseDetails } from '@/jbi-shared/dto/jtms-public-course.dtos';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import BaseButton from '@/jbi-shared/vue-components/BaseButton.vue';
import BaseSwitch from '@/jbi-shared/vue-components/BaseSwitch.vue';
import { PackageSelection } from '@/store/modules/jtms/short-course-checkout/short-course-checkout.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import { ToastProgrammatic as Toast } from 'buefy';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, State } from 'vuex-class';
import PackageSelectionModal from '../Product/ShortCourses/PackageSelectionModal.vue';
import ProceedToLoginConfirmationModal from '../Product/ShortCourses/ProceedToLoginConfirmationModal.vue';
import CallToActionCard from './CallToActionCard.vue';
import BannerImage from './components/BannerImage.vue';
import CourseInformation from './CourseInformation.vue';
import DashboardLayout from './layout/DashboardLayout.vue';

@Component({
  components: {
    Container,
    BaseLoading,
    BaseButton,
    BannerImage,
    BaseSwitch,
    DashboardLayout,
    CourseInformation,
    CallToActionCard
  }
})
export default class ShortCourseDetailsPage extends Vue {
  @Getter('course/isFreeCourse')
  isFreeCourse!: boolean;

  @Getter('course/isGroupRegistrationEnabled')
  groupRegistrationEnabled!: boolean;

  @Getter('course/hasCourseModules')
  hasCourseModules!: boolean;

  @Getter('course/currency')
  currency!: string;

  @Action('auth/silentSessionHealthCheck')
  silentSessionHealthCheck!: () => void;

  @Action('course/getFullCourseDetailsBySlug')
  getFullCourseDetailsBySlug!: (slug: string) => void;

  @Action('shortCourseCheckout/updatePackageSelections')
  updatePackageSelections!: (payload: PackageSelection[]) => void;

  @State(({ auth }: RootState) => auth.apiState.checkSessionHealth)
  silentSessionHealthCheckApiState!: ApiState;

  @State(({ auth }: RootState) => auth.isLoggedIn)
  isLoggedIn!: boolean;

  @State(({ course }: RootState) => course.apiState.getFullCourseDetailsBySlug)
  getFullCourseDetailsBySlugApiState!: ApiState;

  @State(({ course }: RootState) => course.publicCourseState.courseDetails)
  publicCourseDetails!: PublicCourseDetails;

  @State(
    ({ shortCourseCheckout }: RootState) => shortCourseCheckout.selectedPackages
  )
  selectedPackages!: PackageSelection[];

  courseSlug: string = '';

  // Use for seat tracking for free courses (number stepper component in CallToActionCard)
  quantity: number = 1;

  get isGroupRegistrationEnabledForPaidCourseWithoutModules() {
    return (
      this.groupRegistrationEnabled &&
      !this.isFreeCourse &&
      !this.hasCourseModules
    );
  }

  get shouldTriggerPackageSelection() {
    return (
      this.hasCourseModules ||
      (this.groupRegistrationEnabled && !this.isFreeCourse)
    );
  }

  // Due to blocker from JTMS-235, seats currently reflect overall course size.
  // In future, this should take into account registered participants in the course
  // for calculation of ACTUAL remaining seats.
  get seats(): number {
    // Group registration disabled = individual registration
    // Limit seat count to 1.
    if (!this.groupRegistrationEnabled) {
      return 1;
    }

    return this.publicCourseDetails?.courseSize ?? 0;
  }

  get courseName(): string {
    return this.publicCourseDetails?.courseName ?? 'Course name not provided';
  }

  get formattedDateRange(): string {
    if (!this.publicCourseDetails) {
      return 'No dates provided.';
    }

    return [
      this.publicCourseDetails.courseStartDate,
      this.publicCourseDetails.courseEndDate
    ].join(' - ');
  }

  created() {
    if (!this.silentSessionHealthCheckApiState.loading) {
      this.silentSessionHealthCheck();
    }

    const splitPaths = window.location.pathname.split('/');
    this.courseSlug = splitPaths.pop()!;
  }

  handleCallToAction() {
    // Individual Registration for Courses without Modules (no multiple packages)
    // This will not prompt package selection, on click assumed selection quantity = 1
    if (!this.groupRegistrationEnabled && !this.hasCourseModules) {
      // Cloning array to avoid mutating props
      const clonedArray: PackageSelection[] = clone(this.selectedPackages);
      clonedArray.forEach((pkg) => (pkg.quantity = 1));
      this.updatePackageSelections(clonedArray);
    }

    // Group Registration for Courses without Modules (no multiple packages)
    // This will not prompt package selection, on click assumed selection quantity = stepper quantity
    if (this.groupRegistrationEnabled && !this.hasCourseModules) {
      // Cloning array to avoid mutating props
      const clonedArray: PackageSelection[] = clone(this.selectedPackages);
      clonedArray.forEach((pkg) => (pkg.quantity = this.quantity));
      this.updatePackageSelections(clonedArray);
    }

    /**
     * FIXME/ENHANCEMENT:
     * Currently, package selection does not require user login, only checkout route does.
     * If user is guest, on "Continue", application will redirect to login page will clear
     * all existing state, meaning user selections will be reset.
     *
     * Ideally, find a flow to persist package selection state throughout redirection:
     * 1. Store selections in browser local storage
     * 2. New UX flow for login on checkout (open login in new tab, await for validation callback
     *    to navigate to the page instead)
     */
    if (this.shouldTriggerPackageSelection) {
      this.$buefy.modal.open({
        parent: this,
        component: PackageSelectionModal,
        hasModalCard: true, // added to prevent breaking on mobile
        trapFocus: true,
        fullScreen: false,
        canCancel: false,
        props: {
          seats: this.seats,
          currency: this.currency,
          courseName: this.courseName,
          formattedDateRange: this.formattedDateRange,
          shouldDisplayCourseAsPackage: this
            .isGroupRegistrationEnabledForPaidCourseWithoutModules
        },
        events: {
          continue: () => {
            this.handleRedirect();
          }
        }
      });
      return;
    }

    this.handleRedirect();
  }

  handleRedirect() {
    if (!this.isLoggedIn) {
      this.$buefy.modal.open({
        parent: this,
        component: ProceedToLoginConfirmationModal,
        hasModalCard: true, // added to prevent breaking on mobile
        trapFocus: true,
        fullScreen: false,
        canCancel: false,
        events: {
          proceedToLogin: () => {
            this.$router.push({
              name: 'ShortCourseCheckout',
              params: { slug: this.courseSlug }
            });
          }
        }
      });
      return;
    }

    this.$router.push({
      name: 'ShortCourseCheckout',
      params: { slug: this.courseSlug }
    });
  }

  @Watch('courseSlug')
  onCourseSlugRetrievedFromRoute() {
    if (this.courseSlug && !this.publicCourseDetails) {
      this.getFullCourseDetailsBySlug(this.courseSlug);
    }
  }

  @Watch('getFullCourseDetailsBySlugApiState', { immediate: true })
  onFullCourseDetailsCalled(state: ApiState) {
    if (state.error) {
      Toast.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message:
          'Something went wrong. Could not retrieve course details for this course.'
      });
    }
  }
}
